import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { Alert, Box, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { FileDropZone } from "../../../components/UIToolkit/Inputs/FileDropZone";
import { SystemAutoComplete, type SystemAutoCompleteOption } from "../../../components/UIToolkit/Inputs/SystemAutoComplete/SystemAutoComplete";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown/types";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../../../components/UIToolkit/SystemButton/SystemButton";
import { SystemDataGrid } from "../../../components/UIToolkit/Tables/SystemDataGrid";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import { downloadBlob } from "../../../utils/downloadBlob";
import type { PbjAuditEmployeeRow, PbjOverrideImportPreview, PbjOverrideImportRow, PbjOverrideImportRowStatus } from "../../../utils/models";
import { useEditPbjEmployeeOverrideId, useEditPbjEmployeePayType } from "../hooks/usePbjAudit";
import { PBJ_MODEL_QUERY_KEY } from "../hooks/usePbjModel";
import { displayedPbjId } from "./EmployeeIdOverrideCell";

type Props = {
	open: boolean;
	onClose: () => void;
	departmentId: string;
	rows: PbjAuditEmployeeRow[];
};

type Mode = "single" | "upload";
type UploadStep = "upload" | "preview";

// The three real Pay Type Code choices plus a sentinel for "no override / leave synced".
type PayTypeChoice = "1" | "2" | "3" | "none";

// Status → theme chip color (no hardcoded hex — MUI palette tokens): new=success, updated=info,
// removed=error, unchanged=neutral. Same mapping as the manual-hours import diff.
const STATUS_COLOR: Record<PbjOverrideImportRowStatus, "success" | "info" | "error" | "default"> = {
	new: "success",
	updated: "info",
	removed: "error",
	unchanged: "default",
};

const SummaryTile: FC<{ label: string; value: number | string }> = ({ label, value }) => (
	<Box sx={{ flex: "1 1 90px", p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.5, textAlign: "center" }}>
		<Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
			{value}
		</Typography>
		<Typography variant="caption" color="text.secondary">
			{label}
		</Typography>
	</Box>
);

/** Renders "old → new" when the value changed, else the current value, or an em-dash when both empty. */
const renderChange = (oldValue: string | null, newValue: string | null): string => {
	if (oldValue != null && oldValue !== newValue) return `${oldValue || "—"} → ${newValue || "—"}`;
	return newValue || oldValue || "—";
};

// Blank sample file the analyst fills in — the editable columns the importer acts on (Name is an
// optional echo column, so it's omitted here). Example rows are illustrative placeholders
// (obviously-fake ids) meant to be overwritten; Pay Type Code shows all valid values (1=Exempt/W2,
// 2=Non-Exempt/W2, 3=Contract/1099).
const OVERRIDES_TEMPLATE_CSV = ["ID,Override ID,Pay Type Code", "100001,,1", "100002,200002,3"].join("\n");
const OVERRIDES_TEMPLATE_FILENAME = "pbj_employee_overrides_template.csv";
const CSV_MIME = "text/csv;charset=utf-8;";

/**
 * "Manage overrides" — the unified table-level path for the PBJ Employees roster's per-employee
 * overrides (Employee ID + Pay Type Code). Two modes:
 *  - Single employee: pick a synced employee, edit its id override + Pay Type Code, save both
 *    (only the fields that actually changed are PATCHed).
 *  - Upload file: dry-run diff an uploaded overrides file, preview the new/updated/removed rows,
 *    then commit. Block-severity warnings gate the commit.
 * The inline id-override cell on the roster stays as-is; this is an additional entry point.
 */
export const ManageOverridesDialog: FC<Props> = ({ open, onClose, departmentId, rows }) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { track, trackError } = useAnalytics();
	const queryClient = useQueryClient();
	const idMutation = useEditPbjEmployeeOverrideId();
	const payTypeMutation = useEditPbjEmployeePayType();

	const [mode, setMode] = useState<Mode>("single");

	// --- Mode A (single employee) state ---
	const [selectedPbjId, setSelectedPbjId] = useState<string | null>(null);
	const [idDraft, setIdDraft] = useState("");
	const [payTypeDraft, setPayTypeDraft] = useState<PayTypeChoice>("none");

	// --- Mode B (upload) state ---
	const [uploadStep, setUploadStep] = useState<UploadStep>("upload");
	const [file, setFile] = useState<File | null>(null);
	const [preview, setPreview] = useState<PbjOverrideImportPreview | null>(null);
	const [isRunning, setIsRunning] = useState(false);

	// Only synced rows have a server override key (load-only/manual rows have no pbjId), mirroring the
	// inline cell's Boolean(row.pbjId) guard.
	const overridableRows = useMemo(() => rows.filter((r) => Boolean(r.pbjId)), [rows]);

	const employeeOptions: SystemAutoCompleteOption<string>[] = useMemo(
		() => overridableRows.map((r) => ({ label: `${r.name} (${displayedPbjId(r)})`, value: r.pbjId })),
		[overridableRows],
	);

	const selectedRow = useMemo(() => overridableRows.find((r) => r.pbjId === selectedPbjId) ?? null, [overridableRows, selectedPbjId]);

	// Append the CMS mapping number to each choice (e.g. "Contract (3)") so the analyst sees the exact
	// code that lands in the export — the number is language-agnostic, so it's concatenated, not translated.
	const payTypeItems: DropdownItem<PayTypeChoice>[] = useMemo(
		() => [
			{ value: "none", label: t("pbj_model.override_manage.single.pay_type_none") },
			{ value: "1", label: `${t("pbj_model.audit.employees.pay_type.exempt")} (1)` },
			{ value: "2", label: `${t("pbj_model.audit.employees.pay_type.non_exempt")} (2)` },
			{ value: "3", label: `${t("pbj_model.audit.employees.pay_type.contract")} (3)` },
		],
		[t],
	);

	// Client-side download of the blank overrides template (no round-trip needed — static columns).
	const handleDownloadTemplate = useCallback(() => {
		const bytes = new TextEncoder().encode(OVERRIDES_TEMPLATE_CSV);
		downloadBlob(bytes.buffer as ArrayBuffer, OVERRIDES_TEMPLATE_FILENAME, CSV_MIME);
		track("pbj_manage_overrides_template_downloaded", { department_id: departmentId });
	}, [track, departmentId]);

	// Prefill the drafts whenever a different employee is selected.
	useEffect(() => {
		if (!selectedRow) {
			setIdDraft("");
			setPayTypeDraft("none");
			return;
		}
		setIdDraft(selectedRow.pbjOverrideEmployeeId ?? "");
		// Prefill the OVERRIDE, not the effective code — "none" must mean "no override set" so the analyst
		// can see whether one exists and can clear it, rather than seeing the synced code pre-selected.
		setPayTypeDraft(selectedRow.payTypeCodeOverride ?? "none");
	}, [selectedRow]);

	const resetUpload = useCallback(() => {
		setUploadStep("upload");
		setFile(null);
		setPreview(null);
	}, []);

	const handleClose = useCallback(() => {
		if (isRunning || idMutation.isPending || payTypeMutation.isPending) return;
		setMode("single");
		setSelectedPbjId(null);
		resetUpload();
		onClose();
	}, [isRunning, idMutation.isPending, payTypeMutation.isPending, resetUpload, onClose]);

	useEffect(() => {
		if (open) track("pbj_manage_overrides_opened", { department_id: departmentId });
	}, [open, track, departmentId]);

	// --- Mode A save: PATCH only the fields that actually changed. ---
	const currentId = selectedRow?.pbjOverrideEmployeeId ?? "";
	const trimmedId = idDraft.trim();
	const idChanged = Boolean(selectedRow) && trimmedId !== currentId;
	const currentPayType: PayTypeChoice = selectedRow?.payTypeCodeOverride ?? "none";
	const payTypeChanged = Boolean(selectedRow) && payTypeDraft !== currentPayType;
	const canSaveSingle = Boolean(selectedRow) && (idChanged || payTypeChanged);
	const isSavingSingle = idMutation.isPending || payTypeMutation.isPending;

	const handleSaveSingle = useCallback(async () => {
		if (!selectedRow || !canSaveSingle || isSavingSingle) return;
		try {
			if (idChanged) {
				await idMutation.mutateAsync({ pbjEmployeeId: selectedRow.pbjId, pbjOverrideEmployeeId: trimmedId === "" ? null : trimmedId });
			}
			if (payTypeChanged) {
				await payTypeMutation.mutateAsync({ pbjEmployeeId: selectedRow.pbjId, payTypeCode: payTypeDraft === "none" ? null : payTypeDraft });
			}
			showSnackbar(t("pbj_model.override_manage.single.saved"), "success");
			handleClose();
		} catch {
			// Each mutation already surfaces its own error snackbar; keep the dialog open for a retry.
		}
	}, [
		selectedRow,
		canSaveSingle,
		isSavingSingle,
		idChanged,
		payTypeChanged,
		trimmedId,
		payTypeDraft,
		idMutation,
		payTypeMutation,
		showSnackbar,
		t,
		handleClose,
	]);

	// --- Mode B upload: dry-run preview then commit. ---
	const handlePreview = useCallback(async () => {
		if (!file || isRunning) return;
		setIsRunning(true);
		try {
			const res = await backendService.importPbjOverrides({ departmentId, file, dryRun: true });
			setPreview(res.data);
			setUploadStep("preview");
		} catch (error) {
			showSnackbar(t("pbj_model.override_import.failed"), "error");
			trackError("pbj_manage_overrides_import_error", { department_id: departmentId, phase: "preview", error_message: (error as Error)?.message });
		} finally {
			setIsRunning(false);
		}
	}, [file, isRunning, departmentId, showSnackbar, t, trackError]);

	const handleConfirm = useCallback(async () => {
		if (!file || isRunning || preview?.blocked) return;
		setIsRunning(true);
		try {
			const res = await backendService.importPbjOverrides({ departmentId, file, dryRun: false });
			setPreview(res.data);
			if (res.data.committed) {
				await queryClient.invalidateQueries({ queryKey: [PBJ_MODEL_QUERY_KEY, "audit"] });
				track("pbj_manage_overrides_import_committed", {
					department_id: departmentId,
					new: res.data.summary.new,
					updated: res.data.summary.updated,
					removed: res.data.summary.removed,
				});
				showSnackbar(t("pbj_model.override_import.success"), "success");
				handleClose();
			} else {
				showSnackbar(t("pbj_model.override_import.failed"), "error");
			}
		} catch (error) {
			showSnackbar(t("pbj_model.override_import.failed"), "error");
			trackError("pbj_manage_overrides_import_error", { department_id: departmentId, phase: "confirm", error_message: (error as Error)?.message });
		} finally {
			setIsRunning(false);
		}
	}, [file, isRunning, preview, departmentId, queryClient, track, trackError, showSnackbar, t, handleClose]);

	const columns: GridColDef<PbjOverrideImportRow>[] = useMemo(
		() => [
			{
				field: "status",
				headerName: t("pbj_model.override_import.cols.status"),
				minWidth: 110,
				renderCell: ({ value }) => (
					<Chip size="small" color={STATUS_COLOR[value as PbjOverrideImportRowStatus]} label={t(`pbj_model.override_import.status.${value}`)} />
				),
			},
			{ field: "name", headerName: t("pbj_model.override_import.cols.name"), minWidth: 160, flex: 1 },
			{ field: "pbjEmployeeId", headerName: t("pbj_model.override_import.cols.id"), minWidth: 120, flex: 1 },
			{
				field: "newOverrideId",
				headerName: t("pbj_model.override_import.cols.override_id"),
				minWidth: 140,
				flex: 1,
				valueGetter: (_v, row) => renderChange(row.oldOverrideId, row.newOverrideId),
			},
			{
				field: "newPayTypeCode",
				headerName: t("pbj_model.override_import.cols.pay_type"),
				minWidth: 130,
				valueGetter: (_v, row) => renderChange(row.oldPayTypeCode, row.newPayTypeCode),
			},
		],
		[t],
	);

	const blockers = preview?.warnings.filter((w) => w.severity === "block") ?? [];
	const softWarnings = preview?.warnings.filter((w) => w.severity === "warn") ?? [];

	const modeOptions = [
		{ label: t("pbj_model.override_manage.mode.single"), value: "single" as const, "data-testid": "pbj-manage-overrides-mode-single" },
		{ label: t("pbj_model.override_manage.mode.upload"), value: "upload" as const, "data-testid": "pbj-manage-overrides-mode-upload" },
	];

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth data-testid="pbj-manage-overrides-dialog">
			<DialogTitle sx={{ fontWeight: 700 }}>{t("pbj_model.override_manage.title")}</DialogTitle>
			<DialogContent>
				<Stack spacing={2.5} sx={{ pt: 1 }}>
					<SystemFlatSwitch<Mode>
						options={modeOptions}
						activeIndexOrValue={mode}
						onSwitch={(option) => {
							setMode(option.value);
							resetUpload(); // drop any staged file/preview so flipping modes never shows a stale diff
						}}
						delayCallback={0}
						data-testid="pbj-manage-overrides-mode-switch"
					/>

					{mode === "single" ? (
						<Stack spacing={2.5}>
							<SystemAutoComplete<string>
								fullWidth
								options={employeeOptions}
								value={employeeOptions.find((o) => o.value === selectedPbjId) ?? null}
								onChange={(_e, option) => setSelectedPbjId(option?.value ?? null)}
								textFieldProps={{ label: t("pbj_model.override_manage.single.employee_label") }}
								dataTestId="pbj-manage-overrides-employee"
							/>
							{selectedRow && (
								<>
									<SystemValidationTextField
										value={idDraft}
										onChange={(e) => setIdDraft(e.target.value)}
										label={t("pbj_model.override_manage.single.id_label")}
										placeholder={selectedRow.pbjId}
										defaultHelperText={t("pbj_model.override_manage.single.id_hint")}
										fullWidth
										disabled={isSavingSingle}
										dataTestId="pbj-manage-overrides-id-input"
									/>
									<SystemDropdown<PayTypeChoice>
										items={payTypeItems}
										selectedItem={payTypeDraft}
										onChange={(v) => setPayTypeDraft(v ?? "none")}
										label={t("pbj_model.override_manage.single.pay_type_label")}
										fullWidth
										data-testid="pbj-manage-overrides-pay-type"
									/>
								</>
							)}
						</Stack>
					) : uploadStep === "upload" ? (
						<Stack spacing={2.5}>
							<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
								<Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
									{t("pbj_model.override_import.description")}
								</Typography>
								<SystemButton
									buttonVariant="borderless"
									noFullWidthMobile
									leadingIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
									onClick={handleDownloadTemplate}
									data-testid="pbj-manage-overrides-download-template"
								>
									{t("pbj_model.override_import.download_template")}
								</SystemButton>
							</Box>
							<Alert severity="info">{t("pbj_model.override_import.columns_hint")}</Alert>
							<FileDropZone
								accept=".xlsx,.xls,.csv"
								hint={t("pbj_model.override_import.file_hint")}
								onFiles={(files) => setFile(files[0] ?? null)}
								dataTestId="pbj-manage-overrides-dropzone"
							/>
							{file && (
								<Typography variant="body2" sx={{ fontWeight: 600 }}>
									{file.name}
								</Typography>
							)}
						</Stack>
					) : (
						preview && (
							<Stack spacing={2}>
								<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
									<SummaryTile label={t("pbj_model.override_import.summary.new")} value={preview.summary.new} />
									<SummaryTile label={t("pbj_model.override_import.summary.updated")} value={preview.summary.updated} />
									<SummaryTile label={t("pbj_model.override_import.summary.removed")} value={preview.summary.removed} />
									<SummaryTile label={t("pbj_model.override_import.summary.unchanged")} value={preview.summary.unchanged} />
								</Box>

								{blockers.map((w) => (
									<Alert key={`${w.code}-${w.message}`} severity="error">
										{w.message}
									</Alert>
								))}
								{softWarnings.map((w) => (
									<Alert key={`${w.code}-${w.message}`} severity="warning">
										{w.message}
									</Alert>
								))}

								<Box sx={{ height: 420 }}>
									<SystemDataGrid<PbjOverrideImportRow>
										rows={preview.rows}
										columns={columns}
										getRowId={(r) => r.pbjEmployeeId}
										searchableColumns={["name", "pbjEmployeeId"]}
										defaultPageSize={25}
										containedHeight
										dataTestId="pbj-manage-overrides-preview-table"
									/>
								</Box>
							</Stack>
						)
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<SystemButton
					buttonVariant="borderless"
					noFullWidthMobile
					onClick={handleClose}
					disabled={isRunning || isSavingSingle}
					data-testid="pbj-manage-overrides-cancel"
				>
					{t("cancel")}
				</SystemButton>

				{mode === "single" ? (
					<SystemButton
						buttonVariant="primary"
						noFullWidthMobile
						loading={isSavingSingle}
						disabled={!canSaveSingle}
						tooltip={!canSaveSingle && selectedRow ? t("pbj_model.override_manage.single.no_changes") : undefined}
						onClick={() => void handleSaveSingle()}
						data-testid="pbj-manage-overrides-save"
					>
						{t("pbj_model.override_manage.single.save")}
					</SystemButton>
				) : uploadStep === "preview" ? (
					<>
						<SystemButton
							buttonVariant="borderless"
							noFullWidthMobile
							onClick={() => setUploadStep("upload")}
							disabled={isRunning}
							data-testid="pbj-manage-overrides-back"
						>
							{t("pbj_model.override_import.back")}
						</SystemButton>
						<SystemButton
							buttonVariant="primary"
							noFullWidthMobile
							loading={isRunning}
							disabled={preview?.blocked ?? true}
							onClick={() => void handleConfirm()}
							data-testid="pbj-manage-overrides-confirm"
						>
							{t("pbj_model.override_import.confirm")}
						</SystemButton>
					</>
				) : (
					<SystemButton
						buttonVariant="primary"
						noFullWidthMobile
						loading={isRunning}
						disabled={!file}
						onClick={() => void handlePreview()}
						data-testid="pbj-manage-overrides-preview"
					>
						{t("pbj_model.override_import.preview")}
					</SystemButton>
				)}
			</DialogActions>
		</Dialog>
	);
};
